import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AliasBadge, StatusBadge } from '../components/Badges';
import { ArrowLeft, Send, Shield, AlertTriangle, Lock, Eye } from 'lucide-react';
import { toast } from 'sonner';

export default function DealRoomPage() {
  const { roomId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000); // poll every 5s
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [roomId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const res = await api.get(`/rooms/${roomId}/messages`);
      setRoom(res.data.room);
      setMessages(res.data.messages);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load room');
    } finally {
      setLoading(false);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const res = await api.post(`/rooms/${roomId}/messages`, { text });
      setMessages([...messages, res.data]);
      setText('');
      if (res.data.was_redacted) {
        toast.warning('Contact details were redacted from your message');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  if (loading || !room) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="text-center py-24 text-gray-500 font-mono text-sm">Loading deal room...</div>
      </div>
    );
  }

  const otherAlias = user?.role === 'buyer' ? room.seller_alias : room.buyer_alias;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      <div className="max-w-4xl w-full mx-auto px-6 lg:px-8 py-6 flex-1 flex flex-col">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-4"
          data-testid="dealroom-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {/* Room header */}
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-mono mb-1">
                Masked Deal Room
              </div>
              <div className="font-heading text-lg font-semibold text-gray-900">
                Lead <span className="font-mono">{room.lead_id}</span>
              </div>
            </div>
            <StatusBadge status={room.status} />
          </div>

          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="text-gray-500">Between</span>
            {otherAlias ? <AliasBadge alias={otherAlias} /> : <span className="text-gray-400 text-xs">Buyer not yet engaged</span>}
            <span className="text-gray-400">↔</span>
            <AliasBadge alias={user?.alias} />
          </div>

          <div className="mt-4 pt-4 border-t border-gray-100 bg-amber-50 -mx-5 -mb-5 px-5 py-3 rounded-b-lg flex gap-2 text-xs text-amber-900">
            <Lock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>
              <strong>Clarification only.</strong> Contact details, external links, company names, and off-platform deals are auto-redacted.
            </span>
          </div>
        </div>

        {/* Messages */}
        <div
          ref={scrollRef}
          className="flex-1 bg-gray-50 border border-gray-200 rounded-lg p-4 overflow-y-auto min-h-[400px] max-h-[60vh] space-y-3"
          data-testid="messages-container"
        >
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center text-gray-500 py-12">
              <Shield className="w-10 h-10 text-gray-300 mb-2" />
              <div className="text-sm">No messages yet. Start the conversation with a clarification question.</div>
              <div className="text-xs text-gray-400 mt-2 font-mono">e.g. &quot;Is this lead exclusive?&quot;</div>
            </div>
          ) : (
            messages.map(m => {
              const isMe = m.sender_id === user?.user_id;
              return (
                <div
                  key={m.message_id}
                  className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                  data-testid={`message-${m.message_id}`}
                >
                  <div className={`max-w-[75%] ${isMe ? 'items-end' : 'items-start'} flex flex-col`}>
                    <div className="flex items-center gap-2 mb-1 px-1">
                      <span className="font-mono text-xs text-gray-500">{m.sender_alias}</span>
                      <span className="text-xs text-gray-400">{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <div className={`px-4 py-2.5 rounded-lg text-sm ${
                      isMe
                        ? 'bg-[#0055FF] text-white rounded-br-sm'
                        : 'bg-white border border-gray-200 text-gray-900 rounded-bl-sm'
                    }`}>
                      {m.redacted_text}
                    </div>
                    {m.was_redacted && (
                      <div className="mt-1 flex items-center gap-1 text-xs text-amber-700">
                        <AlertTriangle className="w-3 h-3" />
                        <span>Redacted: {m.flag_reasons?.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Input */}
        <form onSubmit={send} className="mt-4 flex gap-2" data-testid="message-form">
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type a clarification question..."
            className="flex-1 h-11"
            disabled={sending}
            data-testid="message-input"
          />
          <Button
            type="submit"
            disabled={sending || !text.trim()}
            className="h-11 bg-[#0055FF] hover:bg-[#0044CC] text-white"
            data-testid="message-send-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            Send
          </Button>
        </form>

        <div className="mt-2 text-xs text-gray-400 text-center">
          All messages are stored. Bypass attempts are flagged to admin.
        </div>
      </div>
    </div>
  );
}
